/**
 * Volume Profile Common Utilities (Shared with FRVP and AVP)
 * - VPFR Calculation
 * - Cache Manager
 * - Rendering Functions
 */
import { getStrokeDasharrayCanvas } from '@react-financial-charts/core';
import type { strokeDashTypes } from '@react-financial-charts/core/lib/utils/strokeDasharray';
import { max, min, sum } from 'd3-array';
import { formatVolume } from './calculateVolumeBars';
import { rgbToRgbaConvert } from './rgbTorgba';

// ============================================================================
// VPFR Calculation
// ============================================================================

export interface VPFRResult {
  poc: number; // Point of Control price (center of POC row)
  vah: number; // Value Area High
  val: number; // Value Area Low
  pocT: number; // POC row top price
  pocB: number; // POC row bottom price
  levels: number[]; // Price levels array (length = rows + 1)
  upVolumes: number[]; // Up volume per level (length = rows)
  downVolumes: number[]; // Down volume per level (length = rows)
  totalVolumes: number[]; // Total volume per level (length = rows)
  pocRow: number; // POC row index
  highRow: number; // Value Area High row index
  lowRow: number; // Value Area Low row index
}

/**
 * Calculate Fixed Range Volume Profile
 */
export function calculateVPFR(
  candles: Array<{ ohlcv: number[] }>,
  startIndex: number,
  endIndex: number,
  rows: number = 5,
  valueAreaPercent: number = 70,
): VPFRResult {
  const safeStart = Math.max(0, Math.min(startIndex, endIndex));
  const safeEnd = Math.min(candles.length - 1, Math.max(startIndex, endIndex));
  const slice = candles.slice(safeStart, safeEnd + 1);

  if (slice.length === 0) {
    const emptyLevels = Array.from({ length: rows + 1 }, () => 0);
    const emptyVolumes = Array(rows).fill(0);
    return {
      poc: 0,
      vah: 0,
      val: 0,
      pocT: 0,
      pocB: 0,
      levels: emptyLevels,
      upVolumes: emptyVolumes,
      downVolumes: emptyVolumes,
      totalVolumes: emptyVolumes,
      pocRow: 0,
      highRow: 0,
      lowRow: 0,
    };
  }

  const high = slice.map(c => c.ohlcv[2]);
  const low = slice.map(c => c.ohlcv[3]);
  const close = slice.map(c => c.ohlcv[4]);
  const open = slice.map(c => c.ohlcv[1]);
  const volume = slice.map(c => c.ohlcv[5]);

  const top = max(high) || 0;
  const bot = min(low) || 0;
  const step = (top - bot) / rows;
  const levels = Array.from({ length: rows + 1 }, (_, i) => bot + step * i);
  const volumes = Array(rows * 2).fill(0);

  const getVolumeIntersection = (
    y1: number,
    y2: number,
    candleY1: number,
    candleY2: number,
    height: number,
    vol: number,
  ): number => {
    const overlapStart = Math.max(Math.min(y1, y2), Math.min(candleY1, candleY2));
    const overlapEnd = Math.min(Math.max(y1, y2), Math.max(candleY1, candleY2));
    const overlap = Math.max(0, overlapEnd - overlapStart);
    const result = height > 0 ? (overlap * vol) / height : 0;
    return isNaN(result) ? 0 : result;
  };

  for (let i = 0; i < slice.length; i++) {
    const candleHigh = high[i];
    const candleLow = low[i];
    const candleClose = close[i];
    const candleOpen = open[i];
    const candleVolume = volume[i];
    const isGreen = candleClose >= candleOpen;

    const bodyTop = max([candleClose, candleOpen]) || 0;
    const bodyBot = min([candleClose, candleOpen]) || 0;
    const topWick = candleHigh - bodyTop;
    const bottomWick = bodyBot - candleLow;
    const body = bodyTop - bodyBot;

    const totalHeight = 2 * topWick + 2 * bottomWick + body;
    const base = totalHeight > 0 ? candleVolume / totalHeight : 0;
    const bodyVol = body * base;
    const topWickVol = 2 * topWick * base;
    const bottomWickVol = 2 * bottomWick * base;
    const EPS = 1e-12;

    for (let row = 0; row < rows; row++) {
      const levelBottom = levels[row];
      const levelTop = levels[row + 1];
      const levelBottomAdj = row === 0 ? levelBottom - EPS : levelBottom;
      const levelTopAdj = row === rows - 1 ? levelTop + EPS : levelTop;
      const bodyVolInLevel = getVolumeIntersection(levelBottom, levelTopAdj, bodyBot, bodyTop, body, bodyVol);
      const wickVolInLevel =
        getVolumeIntersection(levelBottomAdj, levelTopAdj, bodyTop, candleHigh, topWick, topWickVol) / 2 +
        getVolumeIntersection(levelBottomAdj, levelTopAdj, bodyBot, candleLow, bottomWick, bottomWickVol) / 2;

      if (isGreen) {
        volumes[row] += bodyVolInLevel + wickVolInLevel;
        volumes[row + rows] += wickVolInLevel;
      } else {
        volumes[row] += wickVolInLevel;
        volumes[row + rows] += bodyVolInLevel + wickVolInLevel;
      }
    }
  }

  const totalVolumes = Array(rows).fill(0);
  for (let row = 0; row < rows; row++) {
    totalVolumes[row] = volumes[row] + volumes[row + rows];
  }

  const maxVol = max(totalVolumes) || 0;
  const pocRow = totalVolumes.indexOf(maxVol);
  const totalSum = sum(totalVolumes);

  let highRow = pocRow;
  let lowRow = pocRow;
  let vaTotal = maxVol;

  for (let x = 0; x < rows - 1; x++) {
    if (Math.floor((vaTotal / totalSum) * 100) > valueAreaPercent) break;

    const upperVol = highRow < rows - 1 ? totalVolumes[highRow + 1] : 0;
    const lowerVol = lowRow > 0 ? totalVolumes[lowRow - 1] : 0;

    if (upperVol === 0 && lowerVol === 0) break;

    if (upperVol >= lowerVol) {
      if (Math.floor(((vaTotal + upperVol) / totalSum) * 100) > valueAreaPercent) break;
      vaTotal += upperVol;
      highRow += 1;
    } else {
      if (Math.floor(((vaTotal + lowerVol) / totalSum) * 100) > valueAreaPercent) break;
      vaTotal += lowerVol;
      lowRow -= 1;
    }
  }

  const upVolumes = volumes.slice(0, rows);
  const downVolumes = volumes.slice(rows, rows * 2);

  return {
    poc: levels[pocRow] + step / 2,
    vah: levels[highRow + 1],
    val: levels[lowRow],
    pocT: levels[pocRow + 1],
    pocB: levels[pocRow],
    levels,
    upVolumes,
    downVolumes,
    totalVolumes,
    pocRow,
    highRow,
    lowRow,
  };
}

// ============================================================================
// About Cache
// ============================================================================

/**
 * VPFR cache key interface
 */
export interface VPFRCacheKey {
  // Common
  rowSize: number;
  valueAreaVolume: number;
  dataLength: number;
  // For FRVP
  x1Value?: number;
  x2Value?: number;
  // For AVP
  anchorX?: number;
  lastCandleX?: number;
  lastCandleHash?: string;
}

/**
 * VPFR cache manager class
 */
export class VPFRCacheManager {
  private cachedVPFR: VPFRResult | null = null;
  private cachedKey: VPFRCacheKey | null = null;

  public isCacheValid(key: VPFRCacheKey): boolean {
    if (!this.cachedKey || !this.cachedVPFR) return false;

    if (
      this.cachedKey.rowSize !== key.rowSize ||
      this.cachedKey.valueAreaVolume !== key.valueAreaVolume ||
      this.cachedKey.dataLength !== key.dataLength
    ) {
      return false;
    }

    // FRVP Key Comparison
    if (key.x1Value !== undefined && key.x2Value !== undefined) {
      return this.cachedKey.x1Value === key.x1Value && this.cachedKey.x2Value === key.x2Value;
    }

    // AVP Key Comparison
    if (key.anchorX !== undefined && key.lastCandleX !== undefined) {
      return (
        this.cachedKey.anchorX === key.anchorX &&
        this.cachedKey.lastCandleX === key.lastCandleX &&
        this.cachedKey.lastCandleHash === key.lastCandleHash
      );
    }

    return false;
  }

  public getOrCalculate(
    fullData: Array<{ ohlcv: number[] }>,
    startIndex: number,
    endIndex: number,
    rows: number,
    valueAreaPercent: number,
    cacheKey: VPFRCacheKey,
  ): VPFRResult | null {
    if (this.isCacheValid(cacheKey)) {
      return this.cachedVPFR;
    }

    const result = calculateVPFR(fullData, startIndex, endIndex, rows, valueAreaPercent);
    if (result) {
      this.cachedVPFR = result;
      this.cachedKey = cacheKey;
    }
    return result;
  }

  public getCachedOnly(): VPFRResult | null {
    return this.cachedVPFR;
  }

  public clear(): void {
    this.cachedVPFR = null;
    this.cachedKey = null;
  }
}

/**
 * Generate candle data hash (for real-time update detection)
 */
export function getCandleHash(candle: { ohlcv?: number[] } | null | undefined): string {
  if (!candle?.ohlcv) return '';
  return JSON.stringify(candle.ohlcv);
}

// ============================================================================
// About Rendering
// ============================================================================

export interface VolumeProfileAppearance {
  histogramBox?: string;
  upVolume?: string;
  downVolume?: string;
  valuesColor?: string;
  values?: { show?: boolean; color?: string } | boolean;
  poc?: { show?: boolean; strokeStyle?: string; strokeWidth?: number; strokeDasharray?: strokeDashTypes };
  vah?: { show?: boolean; strokeStyle?: string; strokeWidth?: number; strokeDasharray?: strokeDashTypes };
  val?: { show?: boolean; strokeStyle?: string; strokeWidth?: number; strokeDasharray?: strokeDashTypes };
  [key: string]: unknown;
}

export interface VolumeProfileRenderOptions {
  //  Common settings
  appearance: VolumeProfileAppearance;
  vpfrResult: VPFRResult;
  yScale: (value: number) => number;
  rows: number;

  // Bar placement
  histogramStartX: number;
  histogramEndX: number;
  isRightPlacement: boolean;

  // Line range
  lineLeftX: number;
  lineRightX: number;

  // Background box range
  boxLeftX: number;
  boxRightX: number;
}

/**
 * Drawing a background box
 */
export function drawBackgroundBox(ctx: CanvasRenderingContext2D, options: VolumeProfileRenderOptions): void {
  const { appearance, vpfrResult, yScale, boxLeftX, boxRightX } = options;

  if (!appearance.histogramBox) return;

  ctx.fillStyle = appearance.histogramBox;
  const minY = yScale(vpfrResult.levels[vpfrResult.levels.length - 1]);
  const maxY = yScale(vpfrResult.levels[0]);
  const boxWidth = boxRightX - boxLeftX;
  ctx.fillRect(boxLeftX, minY, boxWidth, maxY - minY);
}

/**
 * Draw a Volume Profile bar
 */
export function drawVolumeProfileBars(
  ctx: CanvasRenderingContext2D,
  options: VolumeProfileRenderOptions,
  volumeScale: (value: number) => number,
): void {
  const { appearance, vpfrResult, yScale, rows, isRightPlacement, histogramStartX, histogramEndX } = options;

  // Volume colors (auto opacity)
  const upBase = appearance.upVolume || 'rgb(38, 198, 218)';
  const downBase = appearance.downVolume || 'rgb(236, 64, 122)';

  const upNormal = rgbToRgbaConvert(upBase, 0.35) || upBase;
  const downNormal = rgbToRgbaConvert(downBase, 0.35) || downBase;
  const upVA = rgbToRgbaConvert(upBase, 0.7) || upBase;
  const downVA = rgbToRgbaConvert(downBase, 0.7) || downBase;

  const anchorX = isRightPlacement ? histogramEndX : histogramStartX;

  for (let i = 0; i < rows; i++) {
    const levelBottom = vpfrResult.levels[i];
    const levelTop = vpfrResult.levels[i + 1];

    const upVol = vpfrResult.upVolumes[i];
    const downVol = vpfrResult.downVolumes[i];
    const totalVol = vpfrResult.totalVolumes[i];

    const yBottom = yScale(levelBottom);
    const yTop = yScale(levelTop);

    const fullBarHeight = Math.abs(yTop - yBottom);
    const barHeight = fullBarHeight - 1;
    const barY = Math.min(yTop, yBottom);

    // Value area check
    const isValueArea = i >= vpfrResult.lowRow && i <= vpfrResult.highRow;
    const scaledX = volumeScale(totalVol);
    const totalWidth = Math.abs(scaledX - anchorX);

    const upWidth = totalVol > 0 ? (upVol / totalVol) * totalWidth : 0;
    const downWidth = totalVol > 0 ? (downVol / totalVol) * totalWidth : 0;

    if (!isRightPlacement) {
      // LEFT: Right from anchor
      if (upWidth > 0) {
        ctx.fillStyle = isValueArea ? upVA : upNormal;
        ctx.fillRect(anchorX, barY, upWidth, barHeight);
      }
      if (downWidth > 0) {
        ctx.fillStyle = isValueArea ? downVA : downNormal;
        ctx.fillRect(anchorX + upWidth, barY, downWidth, barHeight);
      }
    } else {
      // RIGHT: Left from anchor (left-right symmetry)
      if (upWidth > 0) {
        ctx.fillStyle = isValueArea ? upVA : upNormal;
        ctx.fillRect(anchorX - upWidth, barY, upWidth, barHeight);
      }
      if (downWidth > 0) {
        ctx.fillStyle = isValueArea ? downVA : downNormal;
        ctx.fillRect(anchorX - upWidth - downWidth, barY, downWidth, barHeight);
      }
    }
  }
}

/**
 * Drawing POC/VAH/VAL
 */
export function drawVolumeProfileLines(ctx: CanvasRenderingContext2D, options: VolumeProfileRenderOptions): void {
  const { appearance, vpfrResult, yScale, lineLeftX, lineRightX } = options;

  // POC line
  if (appearance.poc?.show !== false) {
    const pocY = yScale(vpfrResult.poc);
    ctx.strokeStyle = appearance.poc?.strokeStyle || '#b72020';
    ctx.lineWidth = appearance.poc?.strokeWidth || 1;
    ctx.setLineDash(getStrokeDasharrayCanvas(appearance.poc?.strokeDasharray || 'Solid'));
    ctx.beginPath();
    ctx.moveTo(lineLeftX, pocY);
    ctx.lineTo(lineRightX, pocY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // VAH line
  if (appearance.vah?.show !== false) {
    const vahY = yScale(vpfrResult.vah);
    ctx.strokeStyle = appearance.vah?.strokeStyle || '#787B86';
    ctx.lineWidth = appearance.vah?.strokeWidth || 1;
    ctx.setLineDash(getStrokeDasharrayCanvas(appearance.vah?.strokeDasharray || 'Solid'));
    ctx.beginPath();
    ctx.moveTo(lineLeftX, vahY);
    ctx.lineTo(lineRightX, vahY);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  // VAL line
  if (appearance.val?.show !== false) {
    const valY = yScale(vpfrResult.val);
    ctx.strokeStyle = appearance.val?.strokeStyle || '#787B86';
    ctx.lineWidth = appearance.val?.strokeWidth || 1;
    ctx.setLineDash(getStrokeDasharrayCanvas(appearance.val?.strokeDasharray || 'Solid'));
    ctx.beginPath();
    ctx.moveTo(lineLeftX, valY);
    ctx.lineTo(lineRightX, valY);
    ctx.stroke();
    ctx.setLineDash([]);
  }
}

/**
 * Draw Values labels (volume of each row)
 */
export function drawVolumeProfileValues(ctx: CanvasRenderingContext2D, options: VolumeProfileRenderOptions): void {
  const { appearance, vpfrResult, yScale, isRightPlacement, histogramStartX, histogramEndX } = options;

  const valuesCfg = appearance.values;
  const showValues = typeof valuesCfg === 'object' ? !!valuesCfg.show : !!valuesCfg;

  if (!showValues) return;

  const valuesColor =
    typeof valuesCfg === 'object' ? (valuesCfg.color ?? '#000000') : (appearance.valuesColor ?? '#000000');

  ctx.fillStyle = valuesColor;

  const textPadding = 6;
  ctx.textAlign = isRightPlacement ? 'right' : 'left';
  ctx.textBaseline = 'middle';

  const textX = isRightPlacement ? histogramEndX - textPadding : histogramStartX + textPadding;

  const rows = vpfrResult.levels.length - 1;

  // Calculate rowHeight as one reference row
  const yBottom0 = yScale(vpfrResult.levels[0]);
  const yTop0 = yScale(vpfrResult.levels[1]);
  const rowHeight = Math.abs(yBottom0 - yTop0);
  let yCenterY = (yBottom0 + yTop0) / 2;

  // Calculate fontSize (limited to font sizes 8 to 20)
  const fontSize = Math.max(8, Math.min(Math.round(rowHeight * 0.65), 20));
  ctx.font = `${fontSize}px sans-serif`;

  // Row text rendering
  // Left: "up x down", Right: "down x up"
  for (let i = 0; i < rows; i++) {
    const up = vpfrResult.upVolumes[i] ?? 0;
    const down = vpfrResult.downVolumes[i] ?? 0;
    const text = isRightPlacement
      ? `${formatVolume(down)} x ${formatVolume(up)}`
      : `${formatVolume(up)} x ${formatVolume(down)}`;

    ctx.fillText(text, textX, yCenterY);
    yCenterY -= rowHeight;
  }

  // Summary text at the bottom
  const totalUp = vpfrResult.upVolumes.reduce((s, v) => s + v, 0);
  const totalDown = vpfrResult.downVolumes.reduce((s, v) => s + v, 0);
  const summaryText = isRightPlacement
    ? `${formatVolume(totalDown)} x ${formatVolume(totalUp)}`
    : `${formatVolume(totalUp)} x ${formatVolume(totalDown)}`;

  ctx.fillText(summaryText, textX, yCenterY + rowHeight * vpfrResult.levels.length);
}
